/**
 * Last-in-first-out undo for optimistic writes. Toasts offer the same action; Mod+Z (outside text
 * fields) runs the most recent one. Entries expire after two minutes so an old undo never
 * surprises anyone.
 */
type Entry = { run: () => void; at: number };

const MAX_AGE_MS = 120_000;
const stack: Entry[] = [];

/** Registers an undo and returns a run-once wrapper to hand to the toast as well. */
export function pushUndo(run: () => void): () => void {
  let done = false;
  const once = () => {
    if (done) return;
    done = true;
    run();
  };
  stack.push({ run: once, at: Date.now() });
  if (stack.length > 20) stack.shift();
  return once;
}

/** Runs the most recent undo. Returns false when there is nothing (fresh) to undo. */
export function runUndo(): boolean {
  while (stack.length > 0) {
    const e = stack.pop()!;
    if (Date.now() - e.at > MAX_AGE_MS) continue;
    e.run();
    return true;
  }
  return false;
}
